import React from "react";
import { useGet, usePost } from "../../api";
import {
  checkoutTechnologyType,
  IMerchantSimple,
  MerchantStatusType,
  MerchantType,
} from "../../interfaces";
import { MerchantStatusModal } from "./merchant-status-modal";
import { MerchantDownloadModal } from "./merchant-download-modal";
import {
  Button,
  Table,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Badge,
  Dropdown,
  ButtonGroup,
  Modal,
} from "react-bootstrap";
import {
  FilterBar,
  FilterField,
  PageHeader,
  PageToolbar,
  ToolbarButton,
  ToolbarDivider,
  ToolbarMenu,
  ToolbarMenuHeader,
  ToolbarMenuItem,
} from "../../components/ui";
import { Link, useNavigate } from "react-router";
import { ReactComponent as ShopifyLogo } from "../../images/svg/shopify.svg";
import { MerchantUploadModal } from "./merchant-upload-modal";
import { MerchantTestModal } from "./merchant-test-modal";
import { settingsType, useApi, useSettings } from "../../hooks";

/** Failures that are ours, not the merchant's — the ones worth re-running. */
const RETRY_REASONS: Record<string, string> = {
  proxy: "Proxy refused the request (407)",
  browser_launch: "Chrome would not start",
  timeout: "Run timed out",
  site_unreachable: "Site never served its page",
};

export const Merchants: React.FC = () => {
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(50);
  const [nameFilter, setNameFilter] = React.useState("");
  const [domainFilter, setDomainFilter] = React.useState("");
  const [countryCodeFilter, setCountryCodeFilter] = React.useState("");
  const [updatingMerchantId, setUpdatingMerchantId] = React.useState<
    string | null
  >(null);
  const [onlyScheduled, setOnlyScheduled] = useSettings(
    settingsType.MERCHANTS_ONLY_SCHEDULED
  );
  const [statusTypeFilter, setStatusTypeFilter] = React.useState("");
  const [checkoutTypeFilter, setCheckoutTypeFilter] = React.useState("");
  const [runModeFilter, setRunModeFilter] = React.useState("");
  const [hasSessionFilter, setHasSessionFilter] = React.useState("");
  // Applied immediately like Only Scheduled, rather than waiting for Search —
  // it is a quick way to see the cohort you have opted in.
  const [journeyTestFilter, setJourneyTestFilter] = React.useState("");
  const [appliedFilters, setAppliedFilters] = React.useState({
    name: "",
    domain: "",
    countryCode: "",
    statusType: "",
    checkoutType: "",
    runMode: "",
    hasSession: "",
  });
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [showTestModal, setShowTestModal] = React.useState(false);
  const [showStatusModal, setShowStatusModal] = React.useState(false);
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);
  const [isDownloadingNoCoupons, setIsDownloadingNoCoupons] =
    React.useState(false);
  const [sortBy, setSortBy] = useSettings(settingsType.MERCHANTS_SORT_BY);
  const [sortOrder, setSortOrder] = useSettings(
    settingsType.MERCHANTS_SORT_BY_ORDER
  );
  const [selectedMerchant, setSelectedMerchant] =
    React.useState<IMerchantSimple | null>(null);
  const [selectedMerchantIds, setSelectedMerchantIds] = React.useState<
    Set<string>
  >(new Set());
  const navigate = useNavigate();
  const { get } = useApi();

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (appliedFilters.name) params.append("name", appliedFilters.name);
    if (appliedFilters.domain) params.append("domain", appliedFilters.domain);
    if (appliedFilters.countryCode)
      params.append("countryCode", appliedFilters.countryCode);
    if (appliedFilters.statusType)
      params.append("statusType", appliedFilters.statusType);
    if (appliedFilters.checkoutType)
      params.append("checkoutType", appliedFilters.checkoutType);
    if (appliedFilters.runMode)
      params.append("runMode", appliedFilters.runMode);
    if (appliedFilters.hasSession)
      params.append("hasSession", appliedFilters.hasSession);
    if (onlyScheduled) params.append("onlyScheduled", "true");
    if (journeyTestFilter) params.append("journeyTest", journeyTestFilter);
    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);
    return params.toString();
  };

  const { data: merchants, refetch } = useGet<IMerchantSimple[]>(
    `/merchants?${buildQueryString()}`
  );

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setAppliedFilters({
      name: nameFilter,
      domain: domainFilter,
      countryCode: countryCodeFilter,
      statusType: statusTypeFilter,
      checkoutType: checkoutTypeFilter,
      runMode: runModeFilter,
      hasSession: hasSessionFilter,
    });
    setPage(1); // Reset to first page when filtering
  };

  const clearFilter = (field: keyof typeof appliedFilters) => {
    const setters: Record<keyof typeof appliedFilters, (v: string) => void> = {
      name: setNameFilter,
      domain: setDomainFilter,
      countryCode: setCountryCodeFilter,
      statusType: setStatusTypeFilter,
      checkoutType: setCheckoutTypeFilter,
      runMode: setRunModeFilter,
      hasSession: setHasSessionFilter,
    };
    setters[field]("");
    setAppliedFilters((prev) => ({ ...prev, [field]: "" }));
    setPage(1);
  };

  const clearAllFilters = () => {
    setNameFilter("");
    setDomainFilter("");
    setCountryCodeFilter("");
    setStatusTypeFilter("");
    setCheckoutTypeFilter("");
    setRunModeFilter("");
    setHasSessionFilter("");
    setJourneyTestFilter("");
    // Cleared with the rest so the "active" count and the button agree, even
    // though this one is a sticky setting rather than page state.
    setOnlyScheduled(false);
    setAppliedFilters({
      name: "",
      domain: "",
      countryCode: "",
      statusType: "",
      checkoutType: "",
      runMode: "",
      hasSession: "",
    });
    setPage(1);
  };

  const isFiltered = Object.values(appliedFilters).some(Boolean);
  // Counts what the user has typed or picked, not what is applied — the badge
  // should react to the form, including the two filters that apply instantly.
  const activeFilterCount = [
    nameFilter,
    domainFilter,
    countryCodeFilter,
    statusTypeFilter,
    checkoutTypeFilter,
    runModeFilter,
    hasSessionFilter,
    journeyTestFilter,
    onlyScheduled ? "on" : "",
  ].filter(Boolean).length;

  const startTest = async (merchantId: string, runType: 'live' | 'audit' = 'live') => {
    await get(`/test/browser?merchantId=${merchantId}&runType=${runType}`);

    navigate("/tasks");
  };

  // Unified merchant-map run: builds the map if missing, refreshes + verifies it
  // if present — one mobile agentic walk. Same engine as capture, mobile-emulated.
  const startMapRun = async (merchantId: string) => {
    await get(
      `/test/browser?merchantId=${merchantId}&runType=capture`
    );
    navigate("/tasks");
  };

  const openWithSession = async (merchantId: string) => {
    try {
      setUpdatingMerchantId(merchantId);
      const res = await get<{ message: string }>(
        `/test/open-session?merchantId=${merchantId}`
      );
      window.alert(res?.message ?? "Opened browser with session");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        "Failed to open browser with session";
      window.alert(msg);
    } finally {
      setUpdatingMerchantId(null);
    }
  };

  const updateMerchant = usePost<void, Partial<IMerchantSimple>>();

  // Daily journey (merchant-map) test opt-in. Posts to the dedicated route
  // rather than the general merchant update, because that one $sets the whole
  // additionalSettings subdocument and would wipe the merchant's other settings.
  const setJourneyTest = usePost<
    { _id: string; journeyTestEnabled: boolean },
    { enabled: boolean }
  >();
  const setJourneyTestBulk = usePost<
    { modified: number; enabled: boolean },
    { merchantIds: string[]; enabled: boolean }
  >();

  const toggleJourneyTest = async (merchant: IMerchantSimple) => {
    try {
      setUpdatingMerchantId(merchant._id);
      await setJourneyTest.execute(`/merchants/${merchant._id}/journey-test`, {
        // Reads as ON unless explicitly false, so the first click on an
        // untouched merchant turns it OFF rather than re-enabling it.
        enabled: merchant.additionalSettings?.journeyTestEnabled === false,
      });
      if (refetch) await refetch();
    } catch (error) {
      console.error("Failed to toggle journey test:", error);
    } finally {
      setUpdatingMerchantId(null);
    }
  };

  // Clearing the failure breaker. Deliberately its own endpoint rather than the
  // schedule toggle: re-enabling the schedule by hand would leave
  // runHealth.consecutiveFailures at its tripped value, so the merchant would
  // switch itself off again on the next failure instead of getting a fresh five.
  const resetRunHealth = usePost<{ _id: string; reset: boolean }, {}>();

  const clearFailureBreaker = async (merchant: IMerchantSimple) => {
    try {
      setUpdatingMerchantId(merchant._id);
      await resetRunHealth.execute(
        `/merchants/${merchant._id}/reset-run-health`,
        {}
      );
      if (refetch) await refetch();
    } catch (error) {
      console.error("Failed to reset run health:", error);
    } finally {
      setUpdatingMerchantId(null);
    }
  };

  const [showRequeue, setShowRequeue] = React.useState(false);
  const [requeueReasons, setRequeueReasons] = React.useState<string[]>([]);
  const [requeueBusy, setRequeueBusy] = React.useState(false);
  const { data: retryable, refetch: refetchRetryable } = useGet<{
    sinceHours: number;
    reasons: Array<{ reason: string; merchants: number }>;
  }>(showRequeue ? "/merchants/retryable?sinceHours=24" : null);
  const requeuePost = usePost<{ merchants: number }, any>();

  const runRequeue = async () => {
    if (!requeueReasons.length) return;
    setRequeueBusy(true);
    try {
      const res = await requeuePost.execute("/merchants/requeue", {
        reasons: requeueReasons,
        sinceHours: 24,
      });
      window.alert(
        `${res?.merchants ?? 0} merchant(s) brought forward. The runner picks them up on its next poll.`
      );
      setShowRequeue(false);
      setRequeueReasons([]);
      if (refetch) await refetch();
    } catch {
      window.alert("Could not queue the re-run.");
    } finally {
      setRequeueBusy(false);
    }
  };

  const bulkSetJourneyTest = async (enabled: boolean) => {
    const merchantIds = Array.from(selectedMerchantIds);
    if (merchantIds.length === 0) return;
    try {
      await setJourneyTestBulk.execute("/merchants/journey-test/bulk", {
        merchantIds,
        enabled,
      });
      if (refetch) await refetch();
    } catch (error) {
      console.error("Failed to bulk-toggle journey test:", error);
    }
  };

  const toggleScheduleEnabled = async (merchant: IMerchantSimple) => {
    try {
      setUpdatingMerchantId(merchant._id);

      // If schedule exists, just toggle the enabled field
      // If not, we need to create a minimal schedule object
      const newSchedule = merchant.schedule
        ? {
            enabled: !merchant.schedule.enabled,
            cronExpression: merchant.schedule.cronExpression,
            timezone: merchant.schedule.timezone,
            auditCronExpression: merchant.schedule.auditCronExpression,
          }
        : {
            enabled: true,
            cronExpression: "0 0 * * *",
            timezone: "UTC",
            auditCronExpression: "0 6 * * 0",
          };

      await updateMerchant.execute(`/merchants/${merchant._id}`, {
        schedule: newSchedule,
      });

      // Refresh the merchant list
      if (refetch) await refetch();
    } catch (error) {
      console.error("Failed to toggle schedule:", error);
    } finally {
      setUpdatingMerchantId(null);
    }
  };

  const handleUploadSuccess = async () => {
    // Refresh the merchant list after successful upload
    if (refetch) {
      await refetch();
    }
  };

  const openStatusModal = (merchant: IMerchantSimple) => {
    setSelectedMerchant(merchant);
    setShowStatusModal(true);
  };

  const handleDownload = async () => {
    setShowDownloadModal(true);
  };

  /**
   * CSV of merchants whose last run found no coupons — homepage, domain, geo.
   *
   * Honours the page's country filter, since this is nearly always wanted for
   * one market at a time. Deliberately not part of the Download JSON modal:
   * that produces full merchant records for re-import, and this is a worklist
   * for whoever is sourcing codes.
   */
  const handleDownloadNoCoupons = async () => {
    setIsDownloadingNoCoupons(true);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.countryCode) {
        params.append("countryCode", appliedFilters.countryCode);
      }
      const qs = params.toString();
      const response = await get<Blob>(
        `merchants/download/no-coupons${qs ? `?${qs}` : ""}`,
        { responseType: "blob" }
      );
      const geo = appliedFilters.countryCode
        ? `-${appliedFilters.countryCode.toUpperCase()}`
        : "";
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.download = `merchants-no-coupons${geo}-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Surfaced the same way the page reports its other failures.
      console.error("Failed to download the no-coupon merchant list", e);
    } finally {
      setIsDownloadingNoCoupons(false);
    }
  };

  const toggleMerchantSelection = (merchantId: string) => {
    const newSelection = new Set(selectedMerchantIds);
    if (newSelection.has(merchantId)) {
      newSelection.delete(merchantId);
    } else {
      newSelection.add(merchantId);
    }
    setSelectedMerchantIds(newSelection);
  };

  const toggleSelectAllMerchants = () => {
    if (merchants && merchants.length > 0) {
      if (selectedMerchantIds.size === merchants.length) {
        setSelectedMerchantIds(new Set());
      } else {
        const allIds = new Set(merchants.map((m) => m._id));
        setSelectedMerchantIds(allIds);
      }
    }
  };

  const getSelectedMerchants = () => {
    if (!merchants) return [];
    return merchants.filter((m) => selectedMerchantIds.has(m._id));
  };

  const renderIcon = (type: checkoutTechnologyType) => {
    switch (type) {
      case checkoutTechnologyType.SHOPIFY:
        return <ShopifyLogo height={18} />;
      default:
        return null;
    }
  };
  const selectedCount = selectedMerchantIds.size;

  return (
    <div>
      <PageHeader
        title="Merchants"
        description="Every domain + geo we run against. Add merchants, schedule their coupon runs and test them in bulk."
      />

      <PageToolbar>
        <ToolbarButton
          label="Add Merchant"
          hint="Create one merchant by hand — domain, country and run settings."
          icon="bi-plus-lg"
          variant="primary"
          to="/merchants/new"
        />
        <ToolbarButton
          label="Test Multiple"
          hint="Queue a coupon run now for a set of merchants, without waiting for their schedule."
          icon="bi-lightning"
          variant="warning"
          onClick={() => setShowTestModal(true)}
        />

        <ToolbarDivider />

        <ToolbarMenu label="Import / Export" icon="bi-arrow-down-up">
          <ToolbarMenuItem
            label="Upload JSON"
            hint="Bulk-create or update merchants from a JSON file."
            icon="bi-upload"
            onClick={() => setShowUploadModal(true)}
          />
          <ToolbarMenuItem
            label="Download JSON"
            hint={
              selectedCount > 0
                ? "Export the selected merchants as full records you can re-import."
                : "Export merchants as full records you can re-import. Tick rows first to export only those."
            }
            icon="bi-download"
            count={selectedCount}
            onClick={handleDownload}
          />
          <ToolbarMenuItem
            label={isDownloadingNoCoupons ? "Preparing CSV…" : "No-coupon CSV"}
            hint="Worklist for code sourcing: merchants whose last run found no coupons, with homepage, domain and geo. Respects the country filter."
            icon="bi-filetype-csv"
            disabled={isDownloadingNoCoupons}
            onClick={handleDownloadNoCoupons}
          />
        </ToolbarMenu>

        <ToolbarMenu
          label="Bulk actions"
          icon="bi-check2-square"
          count={selectedCount}
          disabled={selectedCount === 0}
        >
          <ToolbarMenuHeader>
            {selectedCount} merchant{selectedCount === 1 ? "" : "s"} selected
          </ToolbarMenuHeader>
          <ToolbarMenuItem
            label="Daily test on"
            hint="Turn the daily checkout journey test ON for the selected merchants."
            icon="bi-diagram-3"
            onClick={() => bulkSetJourneyTest(true)}
          />
          <ToolbarMenuItem
            label="Daily test off"
            hint="Turn the daily checkout journey test OFF for the selected merchants."
            icon="bi-diagram-3"
            onClick={() => bulkSetJourneyTest(false)}
          />
        </ToolbarMenu>

        <ToolbarMenu label="More tools" icon="bi-three-dots">
          <ToolbarMenuItem
            label="Re-run failures"
            hint="Bring merchants that failed for our own reasons — proxy, browser, timeout — back into the queue. Applies to the last 24h, not to your selection."
            icon="bi-arrow-clockwise"
            onClick={() => {
              setShowRequeue(true);
              refetchRetryable?.();
            }}
          />
          <ToolbarMenuItem
            label="Edit All (Spreadsheet)"
            hint="Edit merchant fields in a grid, the way you would in a spreadsheet."
            icon="bi-table"
            to="/merchants/edit-all"
          />
          <ToolbarMenuItem
            label="Duplicates"
            hint="Find merchants sharing a domain + geo so you can merge or retire them."
            icon="bi-files"
            to="/merchants/duplicates"
          />
          <ToolbarMenuItem
            label="API quarantine"
            hint="Merchants whose platform API path failed, was re-credentialed, and failed again — grouped by the handler fault behind them."
            icon="bi-hdd-network"
            to="/merchants/api-quarantine"
          />
        </ToolbarMenu>
      </PageToolbar>

      <FilterBar
        onSearch={() => handleSearch()}
        onClear={clearAllFilters}
        activeCount={activeFilterCount}
        summary={
          merchants && (
            <>
              Showing {merchants.length} merchant
              {merchants.length !== 1 ? "s" : ""}
              {isFiltered && <span className="text-info"> (filtered)</span>}
              {" on page "}
              {page}
            </>
          )
        }
        trailing={
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Sort by</span>
            <Form.Select
              size="sm"
              style={{ width: "auto" }}
              value={`${sortBy}_${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split("_");
                setSortBy(field);
                setSortOrder(order as "asc" | "desc");
                setPage(1);
              }}
            >
              <option value="createdAt_desc">Date Created (Newest First)</option>
              <option value="createdAt_asc">Date Created (Oldest First)</option>
              <option value="updatedAt_desc">Last Updated (Newest First)</option>
              <option value="updatedAt_asc">Last Updated (Oldest First)</option>
              <option value="lastCouponCheck_asc">
                Last Coupon Check (Oldest First)
              </option>
              <option value="lastCouponCheck_desc">
                Last Coupon Check (Newest First)
              </option>
            </Form.Select>
          </div>
        }
      >
        <FilterField label="Name">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by merchant name…"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
            {nameFilter && (
              <Button
                variant="outline-secondary"
                onClick={() => clearFilter("name")}
                title="Clear name filter"
              >
                <i className="bi bi-x"></i>
              </Button>
            )}
          </InputGroup>
        </FilterField>

        <FilterField label="Domain">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by domain…"
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
            />
            {domainFilter && (
              <Button
                variant="outline-secondary"
                onClick={() => clearFilter("domain")}
                title="Clear domain filter"
              >
                <i className="bi bi-x"></i>
              </Button>
            )}
          </InputGroup>
        </FilterField>

        <FilterField label="Country code" hint="Two-letter geo, e.g. GB or US.">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by country code…"
              value={countryCodeFilter}
              onChange={(e) => setCountryCodeFilter(e.target.value)}
            />
            {countryCodeFilter && (
              <Button
                variant="outline-secondary"
                onClick={() => clearFilter("countryCode")}
                title="Clear country code filter"
              >
                <i className="bi bi-x"></i>
              </Button>
            )}
          </InputGroup>
        </FilterField>

        <FilterField label="Status">
          <Form.Select
            value={statusTypeFilter}
            onChange={(e) => setStatusTypeFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            {Object.values(MerchantStatusType).map((statusType) => (
              <option key={statusType} value={statusType}>
                {statusType}
              </option>
            ))}
          </Form.Select>
        </FilterField>

        <FilterField label="Platform checkout type">
          <Form.Select
            value={checkoutTypeFilter}
            onChange={(e) => setCheckoutTypeFilter(e.target.value)}
          >
            <option value="">All checkout types</option>
            {Object.values(checkoutTechnologyType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Form.Select>
        </FilterField>

        <FilterField label="Run mode" hint="How the coupon run drives the site.">
          <Form.Select
            value={runModeFilter}
            onChange={(e) => setRunModeFilter(e.target.value)}
          >
            <option value="">All run modes</option>
            <option value="agentic">Agentic (browser navigation)</option>
            <option value="api">API (platform handlers)</option>
            <option value="session">Session (captured session)</option>
          </Form.Select>
        </FilterField>

        <FilterField
          label="Captured session"
          hint="Whether we hold a signed-in session for this merchant."
        >
          <Form.Select
            value={hasSessionFilter}
            onChange={(e) => setHasSessionFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Has session</option>
            <option value="false">No session</option>
          </Form.Select>
        </FilterField>

        <FilterField label="Daily test" hint="Applies as soon as you pick it.">
          <Form.Select
            value={journeyTestFilter}
            onChange={(e) => {
              setJourneyTestFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All merchants</option>
            <option value="on">Daily test on</option>
            <option value="off">Daily test off</option>
          </Form.Select>
        </FilterField>

        <FilterField
          label="Schedule"
          hint="Applies as soon as you toggle it."
          md={4}
        >
          <Form.Check
            type="switch"
            id="only-scheduled-switch"
            className="mt-2"
            label="Only scheduled merchants"
            checked={onlyScheduled}
            onChange={(e) => {
              setOnlyScheduled(e.target.checked);
              setPage(1);
            }}
          />
        </FilterField>
      </FilterBar>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: "40px" }}>
              <Form.Check
                type="checkbox"
                checked={
                  merchants && merchants.length > 0
                    ? selectedMerchantIds.size === merchants.length
                    : false
                }
                onChange={toggleSelectAllMerchants}
                title="Select all visible merchants"
              />
            </th>
            <th>#</th>
            <th>Merchant</th>
            <th>Status</th>
            <th>Schedule</th>
            <th>Daily test</th>
            <th>Latest Run</th>
            <th style={{ minWidth: "220px" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {merchants?.map((merchant, index) => (
            <tr key={merchant._id}>
              <td>
                <Form.Check
                  type="checkbox"
                  checked={selectedMerchantIds.has(merchant._id)}
                  onChange={() => toggleMerchantSelection(merchant._id)}
                />
              </td>
              <td>{index + 1}</td>
              <td>
                <div className="fw-semibold">
                  {merchant.name} {renderIcon(merchant.checkoutType)}
                </div>
                {merchant.homePageUrl && (
                  <a
                    href={merchant.homePageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="small text-muted text-decoration-none d-block text-truncate"
                    style={{ maxWidth: "320px" }}
                  >
                    {merchant.homePageUrl}
                  </a>
                )}
                <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                  {(() => {
                    const effective =
                      merchant.runMode ??
                      (merchant.checkoutType !== checkoutTechnologyType.OTHER
                        ? "api"
                        : "agentic");
                    const bg =
                      effective === "session"
                        ? "dark"
                        : effective === "api"
                          ? "info"
                          : "secondary";
                    return (
                      <>
                        <Badge
                          bg={bg}
                          text={effective === "api" ? "dark" : undefined}
                          title={
                            merchant.runMode
                              ? "Explicitly set"
                              : "Auto — inferred from checkout type"
                          }
                        >
                          {effective}
                          {!merchant.runMode && " (auto)"}
                        </Badge>
                        {merchant.sessionRunId && effective !== "session" && (
                          <i
                            className="bi bi-camera ms-1 text-muted"
                            title="Has a captured session (not in session mode)"
                          />
                        )}
                      </>
                    );
                  })()}
                  {merchant.merchantType && (
                    <Badge bg="light" text="dark">
                      {merchant.merchantType}
                    </Badge>
                  )}
                  {merchant.countryCode && (
                    <Badge bg="light" text="dark">
                      {merchant.countryCode}
                    </Badge>
                  )}
                </div>
              </td>
              <td>
                {merchant.statusType ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {merchant.statusMessage || "No additional details"}
                        {merchant.statusUpdatedAt && (
                          <div className="small">
                            Updated:{" "}
                            {new Date(
                              merchant.statusUpdatedAt
                            ).toLocaleString()}
                          </div>
                        )}
                      </Tooltip>
                    }
                  >
                    <Badge
                      bg={
                        merchant.statusType === MerchantStatusType.HEALTHY
                          ? "success"
                          : // Needs a person, but nothing is broken: a duplicate
                            // is a deliberate switch-off and a stock problem is
                            // the merchant's catalogue, not our template.
                            merchant.statusType ===
                              MerchantStatusType.DUPLICATE ||
                            merchant.statusType === MerchantStatusType.NEW
                          ? "secondary"
                          : merchant.statusType ===
                              MerchantStatusType.NO_COUPONS ||
                            merchant.statusType ===
                              MerchantStatusType.PRODUCT_UNAVAILABLE ||
                            merchant.statusType ===
                              MerchantStatusType.DEACTIVATED_AFTER_FAILURES
                          ? "warning"
                          : "danger"
                      }
                      style={{ cursor: "help" }}
                    >
                      {merchant.statusType}
                    </Badge>
                  </OverlayTrigger>
                ) : (
                  <Badge bg="secondary">No status</Badge>
                )}
              </td>
              <td>
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id={`schedule-toggle-${merchant._id}`}
                    checked={merchant.schedule?.enabled || false}
                    onChange={() => toggleScheduleEnabled(merchant)}
                    className="me-2"
                    disabled={updatingMerchantId === merchant._id}
                  />
                  {updatingMerchantId === merchant._id && (
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  {merchant.runHealth?.deactivatedAt && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`breaker-tooltip-${merchant._id}`}>
                          <strong>Switched off automatically</strong>
                          <br />
                          {merchant.runHealth?.deactivatedReason}
                          <br />
                          Last failure:{" "}
                          {merchant.runHealth?.lastFailureAt
                            ? new Date(
                                merchant.runHealth.lastFailureAt
                              ).toLocaleString()
                            : "unknown"}
                          <br />
                          Resetting clears the failure count and puts it back in
                          the rotation.
                        </Tooltip>
                      }
                    >
                      <Button
                        size="sm"
                        variant="outline-warning"
                        className="me-2 py-0"
                        onClick={() => clearFailureBreaker(merchant)}
                        disabled={updatingMerchantId === merchant._id}
                      >
                        Reset ({merchant.runHealth?.consecutiveFailures ?? 0}{" "}
                        fails)
                      </Button>
                    </OverlayTrigger>
                  )}
                  {merchant.schedule?.enabled && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`schedule-tooltip-${merchant._id}`}>
                          <strong>Live:</strong>{" "}
                          {merchant.schedule?.cronExpression} (
                          {merchant.schedule?.timezone})
                          <br />
                          Next run:{" "}
                          {merchant.schedule?.nextScheduleRun
                            ? new Date(
                                merchant.schedule?.nextScheduleRun
                              ).toISOString()
                            : "Not scheduled"}
                          {merchant.schedule?.auditCronExpression && (
                            <>
                              <br />
                              <strong>Audit:</strong>{" "}
                              {merchant.schedule.auditCronExpression}
                              <br />
                              Next audit:{" "}
                              {merchant.schedule?.auditNextScheduleRun
                                ? new Date(
                                    merchant.schedule.auditNextScheduleRun
                                  ).toISOString()
                                : "Not scheduled"}
                            </>
                          )}
                        </Tooltip>
                      }
                    >
                      <Badge bg="info" style={{ cursor: "help" }}>
                        <small>{merchant.schedule?.cronExpression}</small>
                      </Badge>
                    </OverlayTrigger>
                  )}
                </div>
              </td>
              <td>
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id={`journey-test-toggle-${merchant._id}`}
                    // Opt-out: on unless explicitly turned off, so the 13,924
                    // merchants that have never had the field written read as
                    // included rather than excluded.
                    checked={
                      merchant.additionalSettings?.journeyTestEnabled !== false
                    }
                    onChange={() => toggleJourneyTest(merchant)}
                    className="me-2"
                    disabled={updatingMerchantId === merchant._id}
                    title="Check this merchant's checkout every day and flag anything that stops working"
                  />
                  {merchant.additionalSettings?.journeyTestEnabled &&
                    !merchant.cachedProductPages?.length && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`journey-warn-${merchant._id}`}>
                            No saved product page yet — the first run
                            cold-walks the site to find one, then caches it.
                          </Tooltip>
                        }
                      >
                        <Badge bg="warning" text="dark" style={{ cursor: "help" }}>
                          no product
                        </Badge>
                      </OverlayTrigger>
                    )}
                </div>
              </td>
              <td>
                {merchant.lastCouponCheck ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`last-run-tooltip-${merchant._id}`}>
                        Last checked:{" "}
                        {new Date(merchant.lastCouponCheck).toLocaleString()}
                      </Tooltip>
                    }
                  >
                    <div>
                      {merchant.lastCouponCount !== undefined ? (
                        <Badge
                          bg={
                            merchant.lastCouponCount > 0 ? "success" : "danger"
                          }
                          style={{ cursor: "help" }}
                        >
                          {merchant.lastCouponCount} coupons
                        </Badge>
                      ) : (
                        <Badge bg="secondary">No data</Badge>
                      )}
                      <div className="small text-muted mt-1">
                        {new Date(
                          merchant.lastCouponCheck
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </OverlayTrigger>
                ) : (
                  <Badge bg="secondary">Never run</Badge>
                )}
              </td>
              <td>
                <div className="d-flex flex-nowrap gap-1 align-items-center">
                  <Button
                    as={Link as any}
                    variant="outline-info"
                    size="sm"
                    to={`/merchants/${merchant._id}/edit`}
                    title="Edit"
                  >
                    <i className="bi bi-pencil-square"></i>
                  </Button>
                  <Button
                    as={Link as any}
                    variant="outline-info"
                    size="sm"
                    to={`/merchants/${merchant._id}`}
                    title="View"
                  >
                    <i className="bi bi-eye"></i>
                  </Button>
                  <Dropdown as={ButtonGroup}>
                    <Button
                      variant="dark"
                      size="sm"
                      onClick={() => startTest(merchant._id)}
                      title="Run Live Test"
                    >
                      <i className="bi bi-robot"></i>
                    </Button>
                    <Dropdown.Toggle
                      split
                      variant="dark"
                      size="sm"
                      id={`test-dropdown-${merchant._id}`}
                    />
                    <Dropdown.Menu align="end">
                      <Dropdown.Item onClick={() => startTest(merchant._id, 'live')}>
                        <i className="bi bi-play-circle me-2"></i>Live Test
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => startTest(merchant._id, 'audit')}>
                        <i className="bi bi-search me-2"></i>Audit Test (Pending/Suspended)
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={() => startMapRun(merchant._id)}>
                        <i className="bi bi-diagram-3 me-2"></i>Build / Test Map
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => openWithSession(merchant._id)}
                    disabled={updatingMerchantId === merchant._id}
                    title="Open browser with captured session"
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => openStatusModal(merchant)}
                    title="Update Status"
                  >
                    <i className="bi bi-tag"></i>
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={7} className="text-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
              >
                <i className="bi bi-chevron-left"></i>
              </Button>
              <span className="mx-2">Page {page}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={!merchants || merchants.length < limit}
              >
                <i className="bi bi-chevron-right"></i>
              </Button>
            </td>
          </tr>
        </tfoot>
      </Table>

      {/* Upload Modal */}
      <Modal show={showRequeue} onHide={() => setShowRequeue(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Re-run failures</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            These merchants failed in the last 24 hours for reasons on our side,
            so re-running them is worth it. Selecting a reason brings those
            merchants forward; the runner picks them up on its next poll and
            decides for itself whether each needs a capture or a coupon run.
          </p>
          {(retryable?.reasons ?? [])
            .filter((r) => r.reason !== "unique merchants")
            .map((r) => (
              <Form.Check
                key={r.reason}
                type="checkbox"
                id={`requeue-${r.reason}`}
                disabled={r.merchants === 0}
                label={`${RETRY_REASONS[r.reason] ?? r.reason} — ${r.merchants} merchant(s)`}
                checked={requeueReasons.includes(r.reason)}
                onChange={(e) =>
                  setRequeueReasons((prev) =>
                    e.target.checked
                      ? [...prev, r.reason]
                      : prev.filter((x) => x !== r.reason)
                  )
                }
              />
            ))}
          {!retryable && <div className="text-muted small">Counting…</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRequeue(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            disabled={!requeueReasons.length || requeueBusy}
            onClick={runRequeue}
          >
            {requeueBusy ? "Queueing…" : "Re-run selected"}
          </Button>
        </Modal.Footer>
      </Modal>

      <MerchantUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onSuccess={handleUploadSuccess}
      />

      {/* Test Multiple Modal */}
      <MerchantTestModal
        show={showTestModal}
        onHide={() => setShowTestModal(false)}
        onSuccess={() => navigate("/tasks")}
        merchants={merchants || undefined}
      />

      {/* Status Update Modal */}
      <MerchantStatusModal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        merchant={selectedMerchant}
        onSuccess={refetch}
      />

      {/* Download Modal */}
      <MerchantDownloadModal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        onSuccess={refetch}
        selectedMerchants={getSelectedMerchants()}
      />
    </div>
  );
};
